import { pick } from 'lodash';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallow } from 'zustand/shallow';

import { getRegions } from '../../../../api/Rest/system';
import { ReactComponent as ArrowIcon } from '../../../../assets/graphics/svg/colorable/cloud_skin02_but_arrow_set4s_00.svg';
import { Icons, IconType } from '../../../../constants/icons';
import {
  getRegionInfo,
  Regions,
  RegionsCode,
  WorldwideCode,
} from '../../../../constants/region';
import i18n, { TranslationLocale } from '../../../../i18n';
import { Regions as TranslatedRegions } from '../../../../models/SystemStore/SystemStore';
import { useSessionStore } from '../../../../stores/SessionStore';
import { useUserStore } from '../../../../stores/UserStore';
import { getUiLocale } from '../../../../utils/i18n';
import {
  AvatarNode,
  JoyajoyImage,
  ListItemProps,
  SettingList,
  SvgIcon,
} from '../../../Molecules';

import { SVGIconType } from '../../../../constants/svgIcon';
import './WEAAccountInfoTemplate.css';

type WEAAccountInfoTemplateProps = {
  onClickName: () => void;
  onClickUsername: () => void;
  onClickEmail: () => void;
  onClickPassword: () => void;
  onClickRegions: () => void;
  onClickShare: () => void;
  onPressCopy: () => void;
  referralCode: string;
};

export const WEAAccountInfoTemplate = memo(
  ({
    onClickName,
    onClickUsername,
    onClickEmail,
    onClickPassword,
    onClickRegions,
    onClickShare,
    onPressCopy,
    referralCode,
  }: WEAAccountInfoTemplateProps) => {
    const { t } = useTranslation();
    const [translatedRegions, setTranslatedRegions] =
      useState<TranslatedRegions>({});
    const userId = useSessionStore((state) => state.activeUserId);

    const { name, profilePicture, region, avatar, worksForJoyajoy } =
      useUserStore((state) => {
        const profile = state.getProfile(userId ?? '');
        return {
          ...pick(profile, [
            'name',
            'profilePicture',
            'region',
            'avatar',
            'worksForJoyajoy',
          ]),
        };
      }, shallow);

    // const regionStuff = getRegionInfo(region || null);

    const { username, email, hasActiveWeaMembership } = useUserStore(
      (state) => {
        const account = state.getAccount(userId ?? '');
        return {
          ...pick(account, ['username', 'email', 'hasActiveWeaMembership']),
        };
      },
      shallow,
    );

    useEffect(() => {
      const fetchTranslatedRegions = async () => {
        try {
          const response = await getRegions({
            locale: getUiLocale(i18n.language as TranslationLocale),
          });
          if (response.ok && response.data) {
            // remove the "Worldwide" option
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { worldwide, ...rest } = response.data.regions;
            setTranslatedRegions(rest);
          }
        } catch (error) {
          console.error('Error fetching translated regions:', error);
        }
      };

      void fetchTranslatedRegions();
    }, [i18n.language]);

    const getRegionImage = (regionId: RegionsCode | typeof WorldwideCode) => {
      if (regionId === WorldwideCode) {
        return Regions.find((r) => r.id === WorldwideCode)?.image || '';
      }
      return getRegionInfo(regionId).image || '';
    };

    const arrowIcon = (
      <ArrowIcon
        stroke={'#808080'}
        width={15}
        height={15}
        className="right-stuff"
      />
    );

    const listData: ListItemProps[] = [
      {
        icon: {
          resource: { uri: Icons[IconType.SettingDisplayName] },
          style: {
            filter:
              'brightness(0) sepia(100%) saturate(100%) hue-rotate(190deg)',
          },
        },
        title: t('Display Name'),
        description: name || t('Displayed name not set'),
        rightComponent: arrowIcon,
        onPress: onClickName,
      },
      {
        icon: {
          resource: { uri: Icons[IconType.SettingUsername] },
          style: {
            filter:
              'brightness(0) sepia(100%) saturate(100%) hue-rotate(190deg)',
          },
        },
        title: t('Username'),
        description: username || t('Username not set'),
        rightComponent: arrowIcon,
        onPress: onClickUsername,
      },
      {
        icon: { resource: { uri: Icons[IconType.Email] } },
        title: t('Email'),
        description: '',
        rightComponent: (
          <div className="right-component">
            {email?.isVerified && (
              <SvgIcon
                name={SVGIconType.CircleTick}
                width={20}
                height={20}
                style={{ marginRight: 3 }}
              />
            )}
            {email?.address || t('Email not set')}
            {arrowIcon}
          </div>
        ),
        onPress: onClickEmail,
      },
      {
        icon: {
          resource: { uri: Icons[IconType.Password] },
        },
        title: t('Password'),
        description: '******',
        rightComponent: arrowIcon,
        onPress: onClickPassword,
      },
    ];

    const regionData: ListItemProps[] = [
      {
        icon: {
          resource: {
            uri: Icons[IconType.Earth],
          },
          style: {
            filter:
              'brightness(0) sepia(100%) saturate(100%) hue-rotate(190deg)',
          },
        },
        title: t('Country / Region'),
        description: '',
        rightComponent: (
          <div className="right-component">
            <JoyajoyImage
              resource={{ uri: getRegionImage(region || WorldwideCode) }}
              style={{ width: 28, height: 28, marginRight: 3 }}
            />
            {translatedRegions[region || WorldwideCode]}
            {arrowIcon}
          </div>
        ),
        onPress: onClickRegions,
      },
    ];

    return (
      <div className="WEAaccount-info">
        <h5 className="text-title">{t('Account Information')}</h5>
        {userId && (
          <AvatarNode
            avatar={{
              ...avatar,
              userId,
              source:
                profilePicture && profilePicture.publicUrls?.large
                  ? { uri: profilePicture.publicUrls?.small }
                  : '',
            }}
            size={180}
          />
        )}
        <div className="centered-container">
          <div className="setting-list">
            <SettingList data={listData} />
          </div>
        </div>
        <br />
        <div className="centered-container">
          <div className="setting-list">
            <SettingList data={regionData} />
          </div>
        </div>

        {/* {worksForJoyajoy || hasActiveWeaMembership ? (
          <div className="share-button">
            <div className="code-area">
              <div className="text-code">{t('Referral Code: ')}</div>
              <div className="code">{referralCode}</div>
              <img
                onClick={onPressCopy}
                className="size-icon-s icon-button"
                src={Icons[IconType.Copy]}
              />
            </div>
            <TextButton
              text={t('Share & Refer')}
              size={TextButtonSize.Large}
              fontSize={TextButtonFontSize.Medium}
              variant={TextButtonVariant.WEABlue}
              onClick={onClickShare}
            />
          </div>
        ) : null} */}
      </div>
    );
  },
);
